import { ProductPrice } from "@/features/types";
import {
  FlatProduct,
  FlatSheetTab,
  InflatedProduct,
  InflatedTab,
  isFlatProductImageUrl,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { AppUtils } from "@/features/ui/utils/app-utils";
import { zip } from "lodash";

export class SheetFlattener {
  private flattenPrice(priceName: string, price: ProductPrice | null) {
    return {
      // priceName,
      priceAmount: price?.amount || 0,
      priceCurrency: price?.currency || null,
      isPriceMissing: price?.amount === null || price?.amount === undefined,
    };
  }

  private factory(id: number, index: number): FlatProduct {
    return {
      id,
      rowId: `${id}_${index}`,
      imageUrls: null,
      brandSKUEtc: null,
      genderCategorySubcategory: null,
      colorCodeColorNameMaterial: null,
      priceAmount: 0,
      confirmedPriceInfo: 0,
      priceCurrency: null,
      priceAdjustedByPartial: null,
      sizes: null,
      isFiltered: true,
      searchLinkIcons: null,
      isNew: null,
      aiImageChecked: null,
      applyIva: null,
      ivaValue: null,
    };
  }

  // applyIva/ivaValue 도출 우선순위:
  //  1) 에이전시가 confirmed 컨디션을 가진 상태면 confirmedPriceAdjustedBy
  //  2) 그 외에는 원본 priceAdjustedBy
  private effectiveCondition(
    inflatedProduct: InflatedProduct,
    isBuyer?: boolean
  ) {
    const { priceAdjustedBy, confirmedPriceAdjustedBy } = inflatedProduct;
    return !isBuyer && confirmedPriceAdjustedBy
      ? confirmedPriceAdjustedBy
      : priceAdjustedBy;
  }

  private extractFirst(
    inflatedProduct: InflatedProduct,
    isBuyer?: boolean,
    orderSheetSetStatus?: string
  ): FlatProduct {
    const {
      brandId,
      brandName,
      searchWord,
      sku,
      colorName,
      name,
      imageUrls,
      alternativeImageUrls,
      isConditionFixed,
      gender,
      supplyPrice,
      priceAdjustedBy,
      stockQuantityWithOptionList,
      isFiltered,
      season,
      confirmedPriceAdjustedBy,
      confirmedPrice,
      newStockWithOptionList,
      isNew,
      aiImageChecked
    } = inflatedProduct;

    const next: FlatProduct = {
      ...this.factory(inflatedProduct.id, 0),
      imageUrls: [
        ...imageUrls.map((url) => ({ url, type: "ORIGINAL" })),
        ...alternativeImageUrls.map((url) => ({ url, type: "ALTERNATIVE" })),
      ].filter(isFlatProductImageUrl),
      brandSKUEtc: brandName,
      genderCategorySubcategory: gender,
      colorCodeColorNameMaterial: season,
      ...this.flattenPrice("Price", supplyPrice),
      priceAdjustedByPartial: priceAdjustedBy.by,
      sizes: stockQuantityWithOptionList.map((i) => i.name),
      isFiltered,
      searchWord: searchWord || "",
      sku: sku,
      brandId: brandId,
      brandName: brandName,
      color: colorName,
      isConditionFixed: isConditionFixed || false,
      isNew: isNew,
      aiImageChecked: aiImageChecked
    };

    if (!isBuyer && confirmedPriceAdjustedBy) {
      next.priceAdjustedByPartial = {
        top: confirmedPriceAdjustedBy.by,
        under: priceAdjustedBy.by,
      };
    } else {
      next.priceAdjustedByPartial = priceAdjustedBy.by;
    }

    let confirmedQuantity = 0
    if (newStockWithOptionList) {
      confirmedQuantity = newStockWithOptionList.reduce((acc, cur) => acc + cur.quantity, 0)
      if (isConditionFixed) {
        next.priceAmount = supplyPrice?.amount || 0
      }
    }

    // 합계(row0) = 확정금액(row2, confirmedPrice) × 확정수량.
    // isConditionFixed여도 supplyPrice가 아닌 confirmedPrice를 써야 수수료/IVA가 합계에 반영된다.
    // (확정 단가가 아예 없을 때만 supplyPrice로 폴백)
    let confirmedPriceAmount = confirmedPrice?.amount
    if (!confirmedPriceAmount && supplyPrice) {
      confirmedPriceAmount = supplyPrice.amount
    }

    // 확정 단가를 소수점 둘째 자리로 먼저 반올림한 뒤 수량을 곱한다.
    const roundedConfirmedPriceAmount =
      Math.round((confirmedPriceAmount || 0) * 100) / 100
    next.confirmedPriceInfo = roundedConfirmedPriceAmount * confirmedQuantity
    // applyIva 도출 우선순위:
    //  1) 에이전시가 confirmed 컨디션을 가진 상태면 confirmedPriceAdjustedBy.applyIva
    //  2) 그 외에는 원본 priceAdjustedBy.applyIva
    //  3) 둘 다 nullish면 디폴트 true
    const cond = this.effectiveCondition(inflatedProduct, isBuyer);
    next.applyIva = cond.applyIva === false ? false : true;
    next.ivaValue = cond.ivaValue ?? "/1.22";

    return next;
  }

  private extractSecond(
    inflatedProduct: InflatedProduct,
    isBuyer: boolean,
    orderSheetSetStatus: string
  ): FlatProduct {
    const {
      name,
      note,
      categories,
      costPrice,
      retailPrice,
      supplyPrice,
      confirmedPriceWithoutCondition,
      priceAdjustedBy,
      stockQuantityWithOptionList,
      sku,
      isConditionFixed,
      modelNumber,
      isFiltered,
      colorCode,
      colorName,
      newStockWithOptionList,
      confirmedPriceAdjustedBy,
      orderQuantityWithOptionList,
      isNew,
      aiImageChecked,
      lineSheetProductSkuId,
      isSkuSkipped,
    } = inflatedProduct;

    const search_query = encodeURIComponent(sku || "");

    const next: FlatProduct = {
      ...this.factory(inflatedProduct.id, 1),
      brandSKUEtc: sku,
      genderCategorySubcategory: categories.length > 0 ? categories[0] : null,
      colorCodeColorNameMaterial: colorCode
        ? `[${colorCode}]${colorName || ""}`
        : colorName || "",
      ...this.flattenPrice("Cost", costPrice),
      isConditionFixed: isConditionFixed || false,
      isNew: isNew,
      aiImageChecked: aiImageChecked,
      lineSheetProductSkuId: lineSheetProductSkuId ?? null,
      isSkuSkipped: isSkuSkipped ?? false,
      //priceAdjustedByPartial: priceAdjustedBy.value,
      sizes: stockQuantityWithOptionList.map((i) => i.quantity),
      isFiltered,
      searchLinkIcons: `<div style="display: flex; justify-content: center; gap: 8px;">
  <a href="https://search.shopping.naver.com/search/all?where=all&frm=NVSCTAB&query=${search_query}" target="_blank">
    <img src="${window.location.origin + "/naver_icon.png"}" alt="네이버 검색" width="24" height="24" />
  </a>
  <a href="https://kream.co.kr/search?keyword=${search_query}&tab=products&footer=home" target="_blank">
    <img src="${window.location.origin + "/kream_icon.png"}" alt="크림 검색" width="24" height="24" />
  </a>
</div>`,
    };

    // 공급 기준가 표시는 편집한 confirmedPriceWithoutCondition을 그대로 보여준다.
    // isConditionFixed여도 supplyPrice로 덮어쓰지 않고, 값이 아예 없을 때만 폴백.
    let confirmedPriceWithoutConditionAmount = confirmedPriceWithoutCondition?.amount

    if (!confirmedPriceWithoutConditionAmount && supplyPrice) {
      confirmedPriceWithoutConditionAmount = supplyPrice.amount
    }

    next.confirmedPriceInfo = confirmedPriceWithoutConditionAmount

    if (!isBuyer && confirmedPriceAdjustedBy) {
      next.sizes = orderQuantityWithOptionList.map((i) => i.quantity);
      next.priceAdjustedByPartial = {
        top: confirmedPriceAdjustedBy.value,
        under: priceAdjustedBy.value,
      };
    } else {
      next.priceAdjustedByPartial = priceAdjustedBy.value;
    }

    const cond = this.effectiveCondition(inflatedProduct, isBuyer);
    next.applyIva = cond.applyIva === false ? false : true;
    next.ivaValue = cond.ivaValue ?? "/1.22";

    return next;
  }

  private extractThird(
    inflatedProduct: InflatedProduct,
    isBuyer: boolean,
    orderSheetSetStatus: string
  ): FlatProduct {
    const {
      colorName,
      retailPrice,
      supplyPrice,
      isConditionFixed,
      confirmedPrice,
      firstOrderQuantityWithOptionList,
      prevOrderQuantityWithOptionList,
      orderQuantityWithOptionList,
      name,
      categories,
      isFiltered,
      material,
      note,
      newStockWithOptionList,
      isNew,
      aiImageChecked,
    } = inflatedProduct;

    let etcInfo = "";

    if (!note) {
      etcInfo = name || "";
    } else {
      const orgMadeIn = note.madeIn?.trim() || "";

      const madeIn =
        orgMadeIn && !orgMadeIn.toLowerCase().startsWith("made in")
          ? `Made in ${orgMadeIn}`
          : orgMadeIn;

      const extraValues = Object.values(note)
        .filter((v) => v && v !== orgMadeIn)
        .join(", ");

      etcInfo = [madeIn, name, extraValues]
        .filter(Boolean)
        .join(", ");
    }

    const next: FlatProduct = {
      ...this.factory(inflatedProduct.id, 2),
      brandSKUEtc: etcInfo,
      genderCategorySubcategory: categories.length > 1 ? categories[1] : null,
      colorCodeColorNameMaterial: material,
      ...this.flattenPrice("Retail", retailPrice),
      sizes: orderQuantityWithOptionList.map((i) => i.quantity),
      isFiltered,
      isConditionFixed: isConditionFixed || false,
      isNew: isNew,
      aiImageChecked: aiImageChecked,
    };

    next.confirmedPriceInfo = confirmedPrice?.amount

    if (!isBuyer && newStockWithOptionList) {
      // newStockWithOptionList로 주문서 옵션 영역을 판단하는데 바이어 여부와 오더 상태에 따라 판단하도록 바꿔야함
      next.sizes = newStockWithOptionList.map((i) => i.quantity);
    }

    return next;
  }

  flattenProduct(
    inflatedProduct: InflatedProduct,
    isBuyer?: boolean,
    orderSheetSetStatus?: string
  ): FlatProduct[] {
    return [this.extractFirst, this.extractSecond, this.extractThird].map(
      (f) => {
        return f.call(
          this,
          inflatedProduct,
          isBuyer,
          orderSheetSetStatus
        );
      }
    );
  }

  flattenTab(
    inflatedTab: InflatedTab,
    isBuyer?: boolean,
    orderSheetSetStatus?: string
  ): FlatSheetTab {
    return {
      index: inflatedTab.index,
      name: inflatedTab.name,
      isDirty: false,
      products: inflatedTab.products.flatMap((i) => this.flattenProduct(
        i,
        isBuyer,
        orderSheetSetStatus
      )),
    };
  }

  flattenTabs(
    inflatedTabs: InflatedTab[],
    isBuyer?: boolean,
    orderSheetSetStatus?: string
  ): FlatSheetTab[] {
    return inflatedTabs.map((tab) => {
      return this.flattenTab(
        tab,
        isBuyer,
        orderSheetSetStatus
      );
    });
  }
}
